import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiError, clock, priceNumber, priceSuffix, type PlanView, type ShopPublic, type PaymentsMode } from '../lib/api';
import { TopBar, Notice, Spinner } from '../components/ui';
import { Wordmark } from '../components/Logo';
import { OtpStep } from '../components/Otp';

type ShopInfo = { shop: ShopPublic; plans: PlanView[]; terms_version: string; payments_mode: PaymentsMode };
type Step = 'plan' | 'details' | 'otp' | 'pay';

declare global { interface Window { Moyasar?: { init: (cfg: Record<string, unknown>) => void } } }

export default function Subscribe() {
  const { slug = '' } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState<ShopInfo | null>(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState<Step>('plan');
  const [plan, setPlan] = useState<PlanView | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [existing, setExisting] = useState<boolean | null>(null);
  const [promo, setPromo] = useState(false);
  const [verification, setVerification] = useState('');
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [payment, setPayment] = useState<{ subscription_id: string; payments_mode: 'live' | 'mock'; payment: Record<string, unknown> } | null>(null);

  useEffect(() => {
    api.get<ShopInfo>(`/api/public/shops/${slug}`).then(setInfo).catch(() => setError('هذا المحل غير موجود أو غير مفعّل بعد.'));
  }, [slug]);

  const terms = useMemo(() => {
    if (!plan || !info) return [];
    const t = [
      `الباقة تشمل ${plan.visits} ${plan.visits === 1 ? 'حلاقة' : plan.visits === 2 ? 'حلاقتين' : 'حلاقات'} في الشهر${plan.includes_beard ? ' مع تحديد الذقن في كل زيارة' : ''}.`,
      plan.rollover_line,
      plan.min_gap_days > 0 ? `بين الزيارتين ${plan.min_gap_days} أيام على الأقل.` : 'لا حد أدنى بين الزيارتين.',
      plan.window ? `تُستخدم من ${clock(plan.window.start_min)} إلى ${clock(plan.window.end_min)} فقط.` : 'تُستخدم في كل ساعات عمل المحل.',
      'العضوية مرتبطة برقم جوال واحد، ولا تُحوَّل لشخص آخر، ولا تُستخدم في محل آخر، ولا تُصرف نقدًا.',
      info.payments_mode === 'counter'
        ? `تدفع ${plan.price_label} عند الحلاق في أول زيارة، وتُفعَّل عضويتك لحظة الدفع. لا يُسحب منك أي مبلغ إلكترونيًا، والتجديد كل شهر عند الحلاق أيضًا إن أردت.`
        : info.shop.renewal_mode === 'auto'
        ? 'الاشتراك يتجدد تلقائيًا كل شهر بالسعر نفسه من وسيلة الدفع المحفوظة، ويمكنك إيقافه من بطاقتك في أي وقت فيسري من الدورة التالية.'
        : 'لا يُسحب منك أي مبلغ تلقائيًا. تصلك رسالة قبل نهاية الشهر وتجدد بنفسك من بطاقتك إن أردت.',
      'لك حق العدول خلال 7 أيام من أول دفعة، ويُسترد لك المبلغ بعد حسم قيمة الزيارات المستخدمة بسعر الزيارة العادية.',
      `المبلغ يُدفع إلى ${info.shop.name} مباشرة${info.shop.cr_number ? ` (سجل تجاري ${info.shop.cr_number})` : ''}، والمحل هو الملتزم بتقديم الحلاقات ورد غير المستخدم عند توقف الخدمة. تيمبو مشغّل تقني للنظام وليس طرفًا في عقد الخدمة.`,
      info.shop.vat_registered ? 'الأسعار شاملة ضريبة القيمة المضافة، ويصلك إيصال على واتساب مع ملخص أحكام الاسترجاع.' : 'الأسعار نهائية، ويصلك إيصال عضوية على واتساب مع ملخص أحكام الاسترجاع.',
    ];
    return t;
  }, [plan, info]);

  async function createSubscription() {
    if (!plan) return;
    setBusy(true); setError('');
    try {
      const r = await api.post<{ subscription_id: string; payments_mode: PaymentsMode; payment?: Record<string, unknown>; card_token?: string }>('/api/public/subscribe', { slug, plan_id: plan.id, name, phone, verification_token: verification, accept_terms: true, was_existing_customer: existing ?? undefined, marketing_opt_in: promo });
      if (r.payments_mode === 'counter' && r.card_token) { nav(`/card/${r.card_token}?reserved=1`); return; }
      setPayment(r as { subscription_id: string; payments_mode: 'live' | 'mock'; payment: Record<string, unknown> });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'already_member') { nav(`/card/${(e.body as { card_token: string }).card_token}`); return; }
      setError(e instanceof ApiError ? (e.message_ar ?? e.message) : 'تعذر إنشاء الاشتراك.');
    }
    setBusy(false);
  }
  async function mockPay() {
    if (!payment) return;
    setBusy(true);
    try { const r = await api.post<{ card_token: string }>('/api/public/payments/mock', { subscription_id: payment.subscription_id }); nav(`/card/${r.card_token}?new=1`); }
    catch { setError('تعذر إتمام الدفع التجريبي.'); setBusy(false); }
  }

  useEffect(() => {
    if (!payment || payment.payments_mode !== 'live') return;
    const css = document.createElement('link'); css.rel = 'stylesheet'; css.href = 'https://cdn.moyasar.com/mpf/1.15.0/moyasar.css'; document.head.appendChild(css);
    const js = document.createElement('script'); js.src = 'https://cdn.moyasar.com/mpf/1.15.0/moyasar.js';
    js.onload = () => {
      const p = payment.payment;
      window.Moyasar?.init({ element: '.mysr-form', amount: p.amount, currency: 'SAR', description: p.description, publishable_api_key: p.publishable_api_key, callback_url: p.callback_url, metadata: p.metadata, save_card: p.save_card, methods: ['creditcard', 'applepay'], supported_networks: ['mada', 'visa', 'mastercard'], language: 'ar' });
    };
    document.body.appendChild(js);
    return () => { css.remove(); js.remove(); };
  }, [payment]);

  if (error && !info) return <main className="container py-16"><TopBar /><Notice kind="danger">{error}</Notice></main>;
  if (!info) return <main className="container py-16"><TopBar /><Spinner label="نجهز باقات المحل" /></main>;

  return (
    <main className="min-h-dvh pb-16">
      <TopBar right={<span className="pill">{info.shop.name}</span>} />
      <div className="container max-w-xl">
        <div className="mb-6">
          <div className="eyebrow">اشتراك الحلاقة الشهري في</div>
          <h1 className="mt-1">{info.shop.name}</h1>
          <p className="muted mt-2">تدفع أول الشهر مقابل حلاقاتك، وتصلك بطاقتك على واتساب، ودورك له أولوية عند الكرسي. بلا تطبيق، وبلا حساب.</p>
        </div>

        <Steps step={step} counter={info.payments_mode === 'counter'} />

        {step === 'plan' && (
          <section className="grid gap-4">
            {info.plans.map((p) => (
              <button key={p.id} onClick={() => { setPlan(p); setStep('details'); window.scrollTo({ top: 0 }); }} className={`card p-5 text-start hover:border-ink transition ${p.code === 'morning' ? 'bg-paper-2' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap"><h3>{p.name}</h3>{p.window && <span className="pill pill-ok">{clock(p.window.start_min)} إلى {clock(p.window.end_min)}</span>}</div>
                    <p className="muted mt-1">{p.description}</p>
                    <ul className="mt-2 text-sm muted grid gap-1">
                      {p.min_gap_days > 0 && <li>بين الزيارتين {p.min_gap_days} أيام على الأقل</li>}
                      <li>{p.rollover_line}</li>
                    </ul>
                  </div>
                  <div className="text-end shrink-0">
                    <div className="display num" style={{ fontSize: '2rem' }}>{priceNumber(p.price_label)}</div>
                    <div className="text-xs muted" style={{ maxWidth: '9ch' }}>{priceSuffix(p.price_label).replace('ريالًا', 'ريالًا شهريًا')}</div>
                  </div>
                </div>
              </button>
            ))}
            <p className="muted text-xs">الأسعار يحددها {info.shop.name}. المبلغ يُدفع إلى المحل مباشرة.</p>
          </section>
        )}

        {step === 'details' && plan && (
          <section className="card p-6 grid gap-4">
            <PlanSummary plan={plan} onChange={() => setStep('plan')} />
            <div><label className="label" htmlFor="name">اسمك</label><input id="name" className="field" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" /></div>
            <div><label className="label" htmlFor="phone">رقم جوالك (عليه واتساب)</label><input id="phone" className="field num" dir="ltr" inputMode="tel" placeholder="05xxxxxxxx" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" /></div>
            <fieldset className="grid gap-2">
              <legend className="label">هل تحلق في {info.shop.name} من قبل؟</legend>
              <div className="flex gap-2">
                <button type="button" className={`btn ${existing === true ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setExisting(true)}>نعم، زبون قديم</button>
                <button type="button" className={`btn ${existing === false ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setExisting(false)}>لا، أول مرة</button>
              </div>
            </fieldset>
            <button className="btn btn-primary btn-lg" disabled={name.trim().length < 2 || phone.replace(/\D/g, '').length < 9} onClick={() => setStep('otp')}>متابعة</button>
          </section>
        )}

        {step === 'otp' && (
          <section className="card p-6 grid gap-4">
            <h3>تأكيد رقم الجوال</h3>
            <OtpStep phone={phone} onVerified={(t) => { setVerification(t); setStep('pay'); }} />
            <button className="btn btn-ghost" onClick={() => setStep('details')}>غيّر الرقم</button>
          </section>
        )}

        {step === 'pay' && plan && (
          <section className="grid gap-4">
            <div className="card p-6 grid gap-4">
              <PlanSummary plan={plan} onChange={() => { setPayment(null); setStep('plan'); }} />
              <div>
                <h3>شروط العضوية</h3>
                <ol className="mt-2 grid gap-2 text-[15px] list-decimal ps-5">{terms.map((t) => <li key={t}>{t}</li>)}</ol>
              </div>
              {!payment && (
                <>
                  <label className="flex items-start gap-3 cursor-pointer"><input type="checkbox" className="mt-1 size-5" checked={accept} onChange={(e) => setAccept(e.target.checked)} /><span>قرأت الشروط وأوافق عليها، وأعلم أن المبلغ يُدفع إلى {info.shop.name} مباشرة{info.payments_mode === 'counter' ? ' عند الحلاق' : ''}.</span></label>
                  <label className="flex items-start gap-3 cursor-pointer text-sm"><input type="checkbox" className="mt-1 size-5" checked={promo} onChange={(e) => setPromo(e.target.checked)} /><span>أوافق أن يرسل لي {info.shop.name} عروضه وأخباره على واتساب (اختياري، وتوقفها في أي وقت بإرسال كلمة «إيقاف»). رسائل عضويتك تصلك على كل حال.</span></label>
                  {error && <Notice kind="danger">{error}</Notice>}
                  <button className="btn btn-accent btn-lg" disabled={!accept || busy} onClick={createSubscription}>{busy ? 'لحظة...' : info.payments_mode === 'counter' ? 'احجز عضويتي، وأدفع عند الحلاق' : `ادفع ${plan.price_label}`}</button>
                </>
              )}
            </div>
            {payment && payment.payments_mode === 'mock' && (
              <div className="card p-6 grid gap-3">
                <Notice kind="warn">وضع التجربة: لا تُحصَّل أي مبالغ. اضغط الزر لمحاكاة دفعة ناجحة.</Notice>
                <button className="btn btn-accent btn-lg" disabled={busy} onClick={mockPay}>{busy ? 'لحظة...' : 'محاكاة الدفع'}</button>
              </div>
            )}
            {payment && payment.payments_mode === 'live' && (
              <div className="card p-6"><div className="muted text-sm mb-3">الدفع عبر بوابة ميسر باسم {info.shop.name}. تقبل مدى وفيزا وماستركارد وآبل باي.</div><div className="mysr-form" /></div>
            )}
          </section>
        )}
        <p className="muted text-xs mt-8 flex items-center gap-2">تشغيل تقني <Wordmark height={11} />. {info.payments_mode === 'counter' ? 'الدفع عند الحلاق في المحل، ولا يمر أي مبلغ على تيمبو.' : 'الأموال تذهب إلى المحل مباشرة ولا تمر على تيمبو.'}</p>
      </div>
    </main>
  );
}

function Steps({ step, counter }: { step: Step; counter: boolean }) {
  const steps: { k: Step; t: string }[] = [{ k: 'plan', t: 'الباقة' }, { k: 'details', t: 'بياناتك' }, { k: 'otp', t: 'التحقق' }, { k: 'pay', t: counter ? 'التأكيد' : 'الدفع' }];
  const idx = steps.findIndex((s) => s.k === step);
  return (
    <ol className="flex gap-2 mb-6 text-xs font-semibold" aria-label="الخطوات">
      {steps.map((s, i) => <li key={s.k} className={`flex-1 border-t-[3px] pt-2 ${i <= idx ? 'border-accent text-ink' : 'border-line muted'}`}>{s.t}</li>)}
    </ol>
  );
}

function PlanSummary({ plan, onChange }: { plan: PlanView; onChange: () => void }) {
  return (
    <div className="flex items-center justify-between rounded-[var(--radius-md)] bg-paper-2 px-4 py-3">
      <div><div className="font-bold">{plan.name}</div><div className="text-sm muted">{plan.price_label}</div></div>
      <button className="text-sm underline" onClick={onChange}>تغيير</button>
    </div>
  );
}
